/**
 * Validation Schemas using Zod
 * Redesigned for unified routing_rules system
 */
import { z } from 'zod';

// ============================================================================
// Auth Schemas
// ============================================================================

export const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(1, 'Password is required'),
});

export const registerSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z
    .string()
    .min(6, 'Password must be at least 6 characters'),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ['confirmPassword'],
});

// ============================================================================
// Routing Condition Schemas
// ============================================================================

const timeConditionSchema = z.object({
  type: z.literal('time'),
  start_time: z.string().regex(/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/, 'Invalid time format (HH:MM)'),
  end_time: z.string().regex(/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/, 'Invalid time format (HH:MM)'),
  days: z.array(z.enum(['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'])).min(1, 'Select at least one day'),
  timezone: z.string().optional(),
});

const geographicConditionSchema = z.object({
  type: z.literal('geographic'),
  countries: z.array(z.string().length(2, 'Country code must be 2 characters')).optional(),
  regions: z.array(z.string()).optional(),
  cities: z.array(z.string()).optional(),
});

const deviceConditionSchema = z.object({
  type: z.literal('device'),
  device_types: z.array(z.enum(['mobile', 'desktop', 'tablet'])).optional(),
  operating_systems: z.array(z.enum(['windows', 'macos', 'linux', 'ios', 'android'])).optional(),
  browsers: z.array(z.enum(['chrome', 'firefox', 'safari', 'edge', 'opera'])).optional(),
});

const routingConditionSchema = z.discriminatedUnion('type', [
  timeConditionSchema,
  geographicConditionSchema,
  deviceConditionSchema,
]);

const conditionGroupSchema = z.object({
  operator: z.enum(['AND', 'OR']).optional(),
  items: z.array(routingConditionSchema).min(1, 'Add at least one condition'),
});

// ============================================================================
// URL Helper
// ============================================================================

// Helper to normalize URL (add https:// if no protocol)
const normalizeUrl = (url: string): string => {
  if (!url) return url;
  const trimmed = url.trim();
  if (!/^https?:\/\//i.test(trimmed)) {
    return `https://${trimmed}`;
  }
  return trimmed;
};

// ============================================================================
// Routing Action Schemas
// ============================================================================

const redirectActionSchema = z.object({
  type: z.literal('redirect'),
  url: z.string().refine((url) => {
    // Allow empty during editing
    if (!url || url.trim() === '') return true;
    
    // Normalize and validate
    const normalized = normalizeUrl(url.trim());
    try {
      new URL(normalized);
      return true;
    } catch {
      return false;
    }
  }, 'Please enter a valid URL'),
});

const randomizedUrlSchema = z.object({
  url: z.string().refine((url) => {
    // Allow empty during editing
    if (!url || url.trim() === '') return true;
    
    // Normalize and validate
    const normalized = normalizeUrl(url.trim());
    try {
      new URL(normalized);
      return true;
    } catch {
      return false;
    }
  }, 'Please enter a valid URL'),
  weight: z.number().int().min(1).max(100).optional(),
});

const randomizeActionSchema = z.object({
  type: z.literal('randomize'),
  urls: z.array(randomizedUrlSchema).min(2, 'Add at least 2 URLs for A/B testing'),
});

const imageActionSchema = z.object({
  type: z.literal('image'),
  image_id: z.number().int().positive('Please choose an image'),
});

const routingActionSchema = z.discriminatedUnion('type', [
  redirectActionSchema,
  randomizeActionSchema,
  imageActionSchema,
]);

// ============================================================================
// Routing Rule Schema
// ============================================================================

const routingRuleSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1, 'Rule name is required').max(100),
  priority: z.number().int().min(1),
  enabled: z.boolean().optional(),
  conditions: conditionGroupSchema,
  action: routingActionSchema,
});

const routingRulesConfigSchema = z.object({
  version: z.number().int().optional(),
  rules: z.array(routingRuleSchema).optional(),
});

// ============================================================================
// UTM Parameters Schema
// ============================================================================

const utmParametersSchema = z.object({
  utm_source: z.string().max(255).optional(),
  utm_medium: z.string().max(255).optional(),
  utm_campaign: z.string().max(255).optional(),
  utm_term: z.string().max(255).optional(),
  utm_content: z.string().max(255).optional(),
});

// ============================================================================
// QR Code Schemas
// ============================================================================

const qrStyleSchema = z.object({
  dots_type: z.enum(['square', 'dots', 'rounded', 'classy', 'classy-rounded', 'extra-rounded']).optional(),
  corners_square_type: z.enum(['square', 'dot', 'extra-rounded']).optional(),
  corners_dot_type: z.enum(['square', 'dot']).optional(),
});

// Base schema without refinements for type inference
const qrCreateBaseSchema = z.object({
  title: z.string().max(255).optional(), // Title is now optional, will be auto-generated
  description: z.string().max(1000).optional(),
  fallback_url: z.string()
    .min(1, 'URL is required')
    .transform(normalizeUrl)
    .refine((url) => {
      try {
        new URL(url);
        return true;
      } catch {
        return false;
      }
    }, 'Please enter a valid URL'),
  routing_rules: routingRulesConfigSchema.optional().nullable(),
  custom_domain: z.string().optional(),
  utm_parameters: utmParametersSchema.optional().nullable(),
  password: z.string()
    .optional()
    .refine((val) => !val || val === '' || val.length >= 4, {
      message: 'Password must be at least 4 characters',
    }),
  expires_at: z.string().optional(),
  max_scans: z.any().optional().refine((val) => {
    // Allow undefined, null, or NaN (empty field)
    if (val === undefined || val === null) return true;
    if (typeof val === 'number' && isNaN(val)) return true; // Treat NaN as empty/valid
    // If a value is provided, it must be a positive integer
    if (typeof val === 'number' && Number.isInteger(val) && val > 0) return true;
    return false;
  }, {
    message: 'Must be a positive number',
  }),
  custom_logo_url: z.string().optional().refine((val) => !val || z.string().url().safeParse(val).success, {
    message: 'Invalid URL format'
  }),
  logo_image_id: z.number().int().positive().nullish(),
  fallback_image_id: z.number().int().positive().nullish(),
  background_color: z.string().regex(/^(#[0-9A-Fa-f]{6}|transparent)$/, 'Invalid hex color').optional(),
  foreground_color: z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid hex color').optional(),
  qr_style: qrStyleSchema.optional(),
  tag_ids: z.array(z.number()).optional(),
});

export const qrCreateSchema = qrCreateBaseSchema.refine((data) => {
  // Background and foreground colors should not be the same
  if (data.background_color && data.foreground_color &&
      data.background_color.toLowerCase() === data.foreground_color.toLowerCase()) {
    return false;
  }
  return true;
}, {
  message: "Background and foreground colors must be different to ensure QR code visibility",
  path: ["foreground_color"],
});

export const qrUpdateSchema = z.object({
  title: z.string().max(255).optional(),
  description: z.string().max(1000).nullish(),
  fallback_url: z.string()
    .transform(normalizeUrl)
    .refine((url) => {
      if (!url) return true;
      try {
        new URL(url);
        return true;
      } catch {
        return false;
      }
    }, 'Please enter a valid URL')
    .optional(),
  routing_rules: routingRulesConfigSchema.optional().nullable(),
  custom_domain: z.string().nullish(),
  utm_parameters: utmParametersSchema.optional().nullable(),
  is_active: z.boolean().optional(),
  password: z.string()
    .nullish()
    .refine((val) => !val || val === '' || val.length >= 4, {
      message: 'Password must be at least 4 characters',
    }),
  expires_at: z.string().nullish().refine((val) => {
    // Allow empty string, undefined, null, or valid datetime
    if (!val || val === '' || val === null) return true;
    // Try to parse as date to validate
    const date = new Date(val);
    return !isNaN(date.getTime());
  }, {
    message: 'Invalid date format',
  }),
  max_scans: z.any().optional().refine((val) => {
    // Allow undefined, null, or NaN (empty field)
    if (val === undefined || val === null) return true;
    if (typeof val === 'number' && isNaN(val)) return true; // Treat NaN as empty/valid
    // If a value is provided, it must be a positive integer
    if (typeof val === 'number' && Number.isInteger(val) && val > 0) return true;
    return false;
  }, {
    message: 'Must be a positive number',
  }),
  custom_logo_url: z.string().nullish().refine((val) => {
    if (!val || val === null || val === '') return true;
    return z.string().url().safeParse(val).success;
  }, {
    message: 'Invalid URL format'
  }),
  logo_image_id: z.number().int().positive().nullish(),
  fallback_image_id: z.number().int().positive().nullish(),
  background_color: z.string().regex(/^(#[0-9A-Fa-f]{6}|transparent)$/).nullish(),
  foreground_color: z.string().regex(/^#[0-9A-Fa-f]{6}$/).nullish(),
  qr_style: qrStyleSchema.optional(),
  tag_ids: z.array(z.number()).optional(),
}).refine((data) => {
  // Background and foreground colors should not be the same
  // Only validate if both are provided and not null
  if (data.background_color && data.foreground_color && 
      data.background_color !== null && data.foreground_color !== null &&
      data.background_color.toLowerCase() === data.foreground_color.toLowerCase()) {
    return false;
  }
  return true;
}, {
  message: "Background and foreground colors must be different to ensure QR code visibility",
  path: ["foreground_color"],
});

// Unified schema for the QR code editor page (docs/design/qr-form-redesign.md).
// The editor is edit-only — QR codes are always created via the entry modal
// first (see CreateQRCodeModal), so unlike qrCreateSchema/qrUpdateSchema above
// there's no create/update split to maintain: fallback_url is required here
// the same way it's required at the DB level (NOT NULL) for the life of the
// QR code, not just at creation.
export const qrFormSchema = z.object({
  title: z.string().max(255).optional(),
  description: z.string().max(1000).optional(),
  fallback_url: z.string()
    .min(1, 'URL is required')
    .transform(normalizeUrl)
    .refine((url) => {
      try {
        new URL(url);
        return true;
      } catch {
        return false;
      }
    }, 'Please enter a valid URL'),
  routing_rules: routingRulesConfigSchema.optional().nullable(),
  custom_domain: z.string().optional(),
  utm_parameters: utmParametersSchema.optional().nullable(),
  is_active: z.boolean().optional(),
  password: z.string()
    .optional()
    .refine((val) => !val || val === '' || val.length >= 4, {
      message: 'Password must be at least 4 characters',
    }),
  expires_at: z.string().optional(),
  max_scans: z.any().optional().refine((val) => {
    if (val === undefined || val === null) return true;
    if (typeof val === 'number' && isNaN(val)) return true;
    if (typeof val === 'number' && Number.isInteger(val) && val > 0) return true;
    return false;
  }, {
    message: 'Must be a positive number',
  }),
  custom_logo_url: z.string().optional().refine((val) => !val || z.string().url().safeParse(val).success, {
    message: 'Invalid URL format',
  }),
  logo_image_id: z.number().int().positive().nullish(),
  fallback_image_id: z.number().int().positive().nullish(),
  background_color: z.string().regex(/^(#[0-9A-Fa-f]{6}|transparent)$/, 'Invalid hex color').optional(),
  foreground_color: z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid hex color').optional(),
  qr_style: qrStyleSchema.optional(),
  tag_ids: z.array(z.number()).optional(),
}).refine((data) => {
  if (data.background_color && data.foreground_color &&
      data.background_color.toLowerCase() === data.foreground_color.toLowerCase()) {
    return false;
  }
  return true;
}, {
  message: 'Background and foreground colors must be different to ensure QR code visibility',
  path: ['foreground_color'],
});

export type QRFormData = z.infer<typeof qrFormSchema>;

export const qrTagCreateSchema = z.object({
  name: z.string().min(1).max(100),
  color: z.string().regex(/^#[0-9A-Fa-f]{6}$/).optional(),
});

export const qrUrlChangeSchema = z.object({
  url: z.string().url(),
  reason: z.string().max(500).optional(),
});

// ============================================================================
// Exported Types
// ============================================================================

export type LoginFormData = z.infer<typeof loginSchema>;
export type RegisterFormData = z.infer<typeof registerSchema>;
export type QRCreateFormData = z.infer<typeof qrCreateSchema>;
export type QRUpdateFormData = z.infer<typeof qrUpdateSchema>;
export type RoutingRuleFormData = z.infer<typeof routingRuleSchema>;
export type RoutingRulesConfigFormData = z.infer<typeof routingRulesConfigSchema>;

// Export individual schemas for use in rule builder
export {
  timeConditionSchema,
  geographicConditionSchema,
  deviceConditionSchema,
  routingConditionSchema,
  conditionGroupSchema,
  redirectActionSchema,
  randomizeActionSchema,
  routingActionSchema,
  routingRuleSchema,
  routingRulesConfigSchema,
};
